import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Modal, Form, message, Select, Empty, Input, Upload, Tooltip, Switch, Divider, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import {
  getElectricMetersPage,
  fetchDeviceLatestRxTopics,
  addDevice,
  updateDevice,
  deleteDevice,
  batchDeleteDevices,
  getAllRooms,
  downloadDeviceTemplate,
  batchImportDevices,
  toggleAdminDeviceLock,
  getAdminBYGatewayRelation,
  getAdminBYGatewayChildren,
  bindAdminBYGatewayChild,
  repairAdminBYGatewayChild,
  unbindAdminBYGatewayChild,
  scanAdminBYGatewayHouse,
  repairAdminBYGatewayHouse,
} from '../../api';
import ListToolbar from '../../components/ListToolbar';
import {
  DEVICE_CODE_MATCH_MODE_OPTIONS,
  ELECTRIC_METER_SEARCH_SCOPE_OPTIONS,
  getElectricMeterSearchPlaceholder,
  getDeviceLocationSearchPlaceholder,
} from '../../utils/listSearch';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { isDevice4GMeter, isByManufacturer, formatElectricDetailTypeLabel } from '../../utils/deviceMeter';
import MeterSupplementModal from '../../components/MeterSupplementModal';
import MeterSwitchModal from '../../components/MeterSwitchModal';
import MeterRebootModal from '../../components/MeterRebootModal';
import MeterMeteringFactorModal from '../../components/MeterMeteringFactorModal';
import MeterMeteringTotalModal from '../../components/MeterMeteringTotalModal';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const { Text } = Typography;

const { Option } = Select;
// 设备状态配置（复用原有）
const DEVICE_STATUS_OPTIONS = [
  { value: '1', label: '在线/正常' },
  { value: '0', label: '离线/故障' },
];
// 设备类型选项
const DEVICE_TYPE_OPTIONS = [
  { value: 1, label: '电表' },
  { value: 2, label: '水表' },
  { value: 3, label: '门锁' },
];
// ✅ 固化类型：电表=1（用于列表筛选和显示）
const CURRENT_DEVICE_TYPE = { value: 1, label: '电表' };

/** 上次信号时间展示（API 为 ISO 或 "YYYY-MM-DD HH:mm:ss"） */
const formatLastSignalAt = (v) => {
  if (v == null || v === '') return '-';
  const s = String(v).trim();
  if (!s) return '-';
  return s.replace('T', ' ').slice(0, 19);
};

/** 上次充值时间；空则「从未充值」 */
const formatLastElectricPayAt = (v) => {
  if (v == null || v === '') return '从未充值';
  const s = String(v).trim();
  if (!s) return '从未充值';
  return s.replace('T', ' ').slice(0, 19);
};

const formatDateYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** 4G 电表：厂家 RSSI 原始值（1-33、99）；无解析结果时 '-' */
function formatYjRssiOriginal(record) {
  if (!isDevice4GMeter(record)) return '-';
  const v = record.signal_rssi;
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '-';
}

const ElectricMeterList = () => {
  const [devices, setDevices] = useState([]);
  const [rxTopicByDeviceId, setRxTopicByDeviceId] = useState({});
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchScope, setSearchScope] = useState('all');
  const [matchMode, setMatchMode] = useState('fuzzy');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [appliedSearchScope, setAppliedSearchScope] = useState('all');
  const [appliedMatchMode, setAppliedMatchMode] = useState('fuzzy');
  const [locationText, setLocationText] = useState('');
  const [locationScope, setLocationScope] = useState('all');
  const [locationMatchMode, setLocationMatchMode] = useState('fuzzy');
  const [appliedLocationKeyword, setAppliedLocationKeyword] = useState('');
  const [appliedLocationScope, setAppliedLocationScope] = useState('all');
  const [appliedLocationMatchMode, setAppliedLocationMatchMode] = useState('fuzzy');
  const [roomBindStatus, setRoomBindStatus] = useState('all');
  const [appliedRoomBindStatus, setAppliedRoomBindStatus] = useState('all');
  const [remoteStatus, setRemoteStatus] = useState('all');
  const [appliedRemoteStatus, setAppliedRemoteStatus] = useState('all');
  const [remainStatus, setRemainStatus] = useState('all');
  const [appliedRemainStatus, setAppliedRemainStatus] = useState('all');
  const [meterVendor, setMeterVendor] = useState('all');
  const [appliedMeterVendor, setAppliedMeterVendor] = useState('all');
  const [payStartDate, setPayStartDate] = useState('');
  const [payEndDate, setPayEndDate] = useState('');
  const [boundCount, setBoundCount] = useState(0);
  const [unboundCount, setUnboundCount] = useState(0);
  const [remoteCount, setRemoteCount] = useState(0);
  const [remoteOnlineCount, setRemoteOnlineCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [lockTarget, setLockTarget] = useState(null); // { record, locked, targetLock }
  const [lockSubmitting, setLockSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [supplementDevice, setSupplementDevice] = useState(null);
  const [meteringFactorDevice, setMeteringFactorDevice] = useState(null);
  const [meteringTotalDevice, setMeteringTotalDevice] = useState(null);
  const [electricMeterNeedReadIds, setElectricMeterNeedReadIds] = useState(() => new Set());
  const [switchDevice, setSwitchDevice] = useState(null);
  const [rebootDevice, setRebootDevice] = useState(null);
  /** 民一（BY）补电/开关闸：开发中提示（受控 Modal，避免 Modal.info 静态方法不弹） */
  const [byDevTip, setByDevTip] = useState({ open: false, text: '' });
  /** 显示服务费二次确认：不用 Modal.confirm 静态方法，否则套在编辑弹窗里不弹出 */
  const [serviceFeeConfirmOpen, setServiceFeeConfirmOpen] = useState(false);
  const [byRelationModal, setByRelationModal] = useState({ open: false, target: null, relation: null, children: [] });
  const [byRelationLoading, setByRelationLoading] = useState(false);
  const [byRelationSubmitting, setByRelationSubmitting] = useState(false);
  const [bySelectedMotherId, setBySelectedMotherId] = useState(undefined);
  const [byUnbindTarget, setByUnbindTarget] = useState(null);
  const [byHouseBatch, setByHouseBatch] = useState({ open: false, houseId: 0, houseName: '', loading: false, repairing: false, data: null });
  const watchedCommunicationType = Form.useWatch('communication_type', form);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchPage = async (
    page,
    pageSize,
    keyword = appliedKeyword,
    scope = appliedSearchScope,
    mode = appliedMatchMode,
    locKeyword = appliedLocationKeyword,
    locScope = appliedLocationScope,
    locMode = appliedLocationMatchMode,
    roomBind = appliedRoomBindStatus,
    startDate = payStartDate,
    endDate = payEndDate,
    remote = appliedRemoteStatus,
    remain = appliedRemainStatus,
    vendor = appliedMeterVendor,
  ) => {
    setLoading(true);
    try {
      const res = await getElectricMetersPage({
        page,
        page_size: pageSize,
        keyword,
        match_mode: mode,
        search_scope: scope,
        location_keyword: locKeyword,
        location_match_mode: locMode,
        location_scope: locScope,
        room_bind_status: roomBind,
        remote_status: remote,
        remain_status: remain,
        meter_vendor: vendor,
        start_date: startDate,
        end_date: endDate,
      });
      if (res?.success && Array.isArray(res.data)) {
        setDevices(res.data);
        setRxTopicByDeviceId({});
        setBoundCount(Number(res.bound_count) || 0);
        setUnboundCount(Number(res.unbound_count) || 0);
        setRemoteCount(Number(res.remote_count) || 0);
        setRemoteOnlineCount(Number(res.remote_online_count) || 0);
        setPagination({
          current: res.page || page,
          pageSize: res.page_size || pageSize,
          total: Number(res.total) || 0,
        });
      } else {
        message.error(res?.error || `加载${CURRENT_DEVICE_TYPE.label}列表失败`);
        setDevices([]);
        setBoundCount(0);
        setUnboundCount(0);
        setRemoteCount(0);
        setRemoteOnlineCount(0);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    } catch (error) {
      console.error('加载设备失败：', error);
      const errorMsg = error.response?.data?.error || `加载${CURRENT_DEVICE_TYPE.label}列表失败，请稍后重试`;
      message.error(errorMsg);
      setDevices([]);
      setBoundCount(0);
      setUnboundCount(0);
      setRemoteCount(0);
      setRemoteOnlineCount(0);
    } finally {
      setLoading(false);
    }
  };

  const byGatewayErrorText = (error, fallback = '操作失败，请稍后重试') =>
    error?.response?.data?.error || error?.response?.data?.msg || error?.message || fallback;

  const isBYGatewayMeter = (record) => {
    if (!isByManufacturer(record)) return false;
    const meterNo = String(record?.current_meter_no || record?.imei || record?.factory_meter_no || '').trim();
    return meterNo.startsWith('161') || meterNo.startsWith('172');
  };

  const loadBYGatewayChildren = async (motherDeviceId, { silent = false } = {}) => {
    if (!motherDeviceId) return [];
    const res = await getAdminBYGatewayChildren(motherDeviceId);
    if (!res?.success) throw new Error(res?.error || '查询绑定关系失败');
    const children = Array.isArray(res?.data?.children) ? res.data.children : [];
    setByRelationModal((prev) => ({ ...prev, children }));
    if (!silent) message.success('厂家绑定关系已更新');
    return children;
  };

  const openBYGatewayRelation = async (record) => {
    setByRelationModal({ open: true, target: record, relation: null, children: [] });
    setBySelectedMotherId(undefined);
    setByRelationLoading(true);
    try {
      const res = await getAdminBYGatewayRelation(record.id);
      if (!res?.success) throw new Error(res?.error || '查询子母表关系失败');
      const relation = res.data || {};
      setByRelationModal((prev) => ({ ...prev, relation }));
      if (relation.relation_type === 'mother') {
        await loadBYGatewayChildren(record.id, { silent: true });
      }
    } catch (error) {
      const text = byGatewayErrorText(error, '查询子母表关系失败');
      message.error(text);
      setByRelationModal((prev) => ({ ...prev, relation: { error: text } }));
    } finally {
      setByRelationLoading(false);
    }
  };

  const refreshBYGatewayRelation = async () => {
    const target = byRelationModal.target;
    if (target) await openBYGatewayRelation(target);
  };

  const submitBYGatewayAction = async (action, childDeviceId, parentDeviceId) => {
    if (!childDeviceId) return false;
    setByRelationSubmitting(true);
    try {
      let res;
      if (action === 'bind') res = await bindAdminBYGatewayChild(childDeviceId, parentDeviceId);
      if (action === 'repair') res = await repairAdminBYGatewayChild(childDeviceId, parentDeviceId);
      if (action === 'unbind') res = await unbindAdminBYGatewayChild(childDeviceId);
      if (!res?.success) throw new Error(res?.error || '操作失败');
      message.success(action === 'unbind' ? '解绑成功' : action === 'repair' ? '修复绑定成功' : '绑定成功');
      await refreshBYGatewayRelation();
      fetchPage(pagination.current, pagination.pageSize);
      return true;
    } catch (error) {
      message.error(byGatewayErrorText(error));
      return false;
    } finally {
      setByRelationSubmitting(false);
    }
  };

  const requestBYGatewayUnbind = (deviceId, meterNo) => {
    if (!deviceId) return;
    setByUnbindTarget({ deviceId, meterNo: String(meterNo || '').trim() });
  };

  const confirmBYGatewayUnbind = async () => {
    if (!byUnbindTarget?.deviceId) return;
    const success = await submitBYGatewayAction('unbind', byUnbindTarget.deviceId);
    if (success) setByUnbindTarget(null);
  };

  const openBYHouseBatch = async (record) => {
    const houseId = Number(record?.house_id || 0);
    if (!houseId) {
      message.error('该电表未绑定有效房源，无法批量查询');
      return;
    }
    setByHouseBatch({ open: true, houseId, houseName: record?.house_name || '', loading: true, repairing: false, data: null });
    try {
      const res = await scanAdminBYGatewayHouse(houseId);
      if (!res?.success) throw new Error(res?.error || '房源扫描失败');
      setByHouseBatch((prev) => ({ ...prev, data: res.data }));
    } catch (error) {
      message.error(byGatewayErrorText(error, '房源扫描失败'));
    } finally {
      setByHouseBatch((prev) => ({ ...prev, loading: false }));
    }
  };

  const repairBYHouseBatch = async () => {
    const items = (byHouseBatch.data?.items || [])
      .filter((item) => item.can_repair && item.child_device_id > 0)
      .map((item) => ({ child_device_id: item.child_device_id, parent_device_id: item.parent_device_id }));
    if (!items.length) return;
    setByHouseBatch((prev) => ({ ...prev, repairing: true }));
    try {
      const res = await repairAdminBYGatewayHouse(byHouseBatch.houseId, items);
      if (!res?.success) throw new Error(res?.error || '批量修复失败');
      message.success(`批量修复完成：成功 ${res.data?.success_count || 0} 项，失败 ${res.data?.failed_count || 0} 项`);
      await openBYHouseBatch({ house_id: byHouseBatch.houseId, house_name: byHouseBatch.houseName });
      fetchPage(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(byGatewayErrorText(error, '批量修复失败'));
    } finally {
      setByHouseBatch((prev) => ({ ...prev, repairing: false }));
    }
  };

  // 加载房间列表
  const loadRooms = async () => {
    try {
      const response = await getAllRooms();
      const roomList = response?.success && Array.isArray(response.data) ? response.data : [];
      setRooms(roomList);
    } catch (error) {
      console.error('加载房间失败：', error);
      message.error('加载房间列表失败');
    }
  };

  // 加载电表数据（服务端分页，每次只拉当前页）
  useEffect(() => {
    loadRooms();
    fetchPage(1, 20);
  }, []);

  // 仅当前页 4G 电表按需拉最新 RX 主题（mqtt_heartbeat_raw）
  useEffect(() => {
    const ids = devices.filter(isDevice4GMeter).map((d) => d.id);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    fetchDeviceLatestRxTopics(ids)
      .then((res) => {
        if (cancelled || !res?.success || !res.data) return;
        setRxTopicByDeviceId((prev) => ({ ...prev, ...res.data }));
      })
      .catch((err) => {
        console.error('加载最新RX主题失败：', err);
      });
    return () => {
      cancelled = true;
    };
  }, [devices]);

  const getLatestRxTopic = (record) => rxTopicByDeviceId[String(record.id)] || '';

  const handleSearch = () => {
    const kw = searchText.trim();
    const locKw = locationText.trim();
    setAppliedKeyword(kw);
    setAppliedSearchScope(searchScope);
    setAppliedMatchMode(matchMode);
    setAppliedLocationKeyword(locKw);
    setAppliedLocationScope(locationScope);
    setAppliedLocationMatchMode(locationMatchMode);
    setAppliedRoomBindStatus(roomBindStatus);
    setAppliedRemoteStatus(remoteStatus);
    setAppliedRemainStatus(remainStatus);
    setAppliedMeterVendor(meterVendor);
    fetchPage(1, pagination.pageSize, kw, searchScope, matchMode, locKw, locationScope, locationMatchMode, roomBindStatus, payStartDate, payEndDate, remoteStatus, remainStatus, meterVendor);
  };

  const handleRoomBindStatusChange = (value) => {
    const next = value || 'all';
    setRoomBindStatus(next);
    setAppliedRoomBindStatus(next);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      next,
      payStartDate,
      payEndDate,
      appliedRemoteStatus,
      appliedRemainStatus,
    );
  };

  const handleRemoteStatusChange = (value) => {
    const next = value || 'all';
    setRemoteStatus(next);
    setAppliedRemoteStatus(next);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      payStartDate,
      payEndDate,
      next,
      appliedRemainStatus,
    );
  };

  const handleRemainStatusChange = (value) => {
    const next = value || 'all';
    setRemainStatus(next);
    setAppliedRemainStatus(next);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      payStartDate,
      payEndDate,
      appliedRemoteStatus,
      next,
      appliedMeterVendor,
    );
  };

  const handleMeterVendorChange = (value) => {
    const next = value || 'all';
    setMeterVendor(next);
    setAppliedMeterVendor(next);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      payStartDate,
      payEndDate,
      appliedRemoteStatus,
      appliedRemainStatus,
      next,
    );
  };

  const handleMatchModeChange = (mode) => {
    setMatchMode(mode);
    if (!appliedKeyword) return;
    setAppliedMatchMode(mode);
    fetchPage(1, pagination.pageSize, appliedKeyword, appliedSearchScope, mode, appliedLocationKeyword, appliedLocationScope, appliedLocationMatchMode, appliedRoomBindStatus);
  };

  const handleSearchScopeChange = (scope) => {
    setSearchScope(scope);
    if (!appliedKeyword) return;
    setAppliedSearchScope(scope);
    fetchPage(1, pagination.pageSize, appliedKeyword, scope, appliedMatchMode, appliedLocationKeyword, appliedLocationScope, appliedLocationMatchMode, appliedRoomBindStatus);
  };

  const handleLocationMatchModeChange = (mode) => {
    setLocationMatchMode(mode);
    if (!appliedLocationKeyword) return;
    setAppliedLocationMatchMode(mode);
    fetchPage(1, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode, appliedLocationKeyword, appliedLocationScope, mode, appliedRoomBindStatus);
  };

  const handleLocationScopeChange = (scope) => {
    setLocationScope(scope);
    if (!appliedLocationKeyword) return;
    setAppliedLocationScope(scope);
    fetchPage(1, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode, appliedLocationKeyword, scope, appliedLocationMatchMode, appliedRoomBindStatus);
  };

  const handleReset = () => {
    setSearchText('');
    setSearchScope('all');
    setMatchMode('fuzzy');
    setLocationText('');
    setLocationScope('all');
    setLocationMatchMode('fuzzy');
    setRoomBindStatus('all');
    setRemoteStatus('all');
    setRemainStatus('all');
    setMeterVendor('all');
    setPayStartDate('');
    setPayEndDate('');
    setAppliedKeyword('');
    setAppliedSearchScope('all');
    setAppliedMatchMode('fuzzy');
    setAppliedLocationKeyword('');
    setAppliedLocationScope('all');
    setAppliedLocationMatchMode('fuzzy');
    setAppliedRoomBindStatus('all');
    setAppliedRemoteStatus('all');
    setAppliedRemainStatus('all');
    setAppliedMeterVendor('all');
    fetchPage(1, pagination.pageSize, '', 'all', 'fuzzy', '', 'all', 'fuzzy', 'all', '', '', 'all', 'all', 'all');
  };

  const handlePayStartDateChange = (e) => {
    const start = e.target.value || '';
    setPayStartDate(start);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      start,
      payEndDate,
    );
  };

  const handlePayEndDateChange = (e) => {
    const end = e.target.value || '';
    setPayEndDate(end);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      payStartDate,
      end,
    );
  };

  const handlePayRecentMonths = (months) => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - months);
    const startStr = formatDateYMD(start);
    const endStr = formatDateYMD(end);
    setPayStartDate(startStr);
    setPayEndDate(endStr);
    fetchPage(
      1,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
      startStr,
      endStr,
    );
  };

  const handleRefresh = () => {
    fetchPage(
      pagination.current,
      pagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
    );
  };

  const electricSearchPlaceholder = getElectricMeterSearchPlaceholder(searchScope);
  const locationSearchPlaceholder = getDeviceLocationSearchPlaceholder(locationScope);

  const handleExport = () => {
    const exportData = devices.map((device) => ({
      '电表ID': device.id,
      '设备MAC': device.device_mac || '-',
      '厂商表号': device.factory_meter_no || '-',
      '最新RX主题': getLatestRxTopic(device) || '-',
      '当前表号': device.current_meter_no || '-',
      'IMEI': device.imei || '-',
      'ICCID': device.iccid || '-',
      '信号强度': formatYjRssiOriginal(device),
      '信号强度(换算)': isDevice4GMeter(device) ? (device.signal_strength_bars || '-') : '-',
      '上次信号时间': isDevice4GMeter(device) ? formatLastSignalAt(device.last_signal_at) : '-',
      '上次充值时间': formatLastElectricPayAt(device.last_electric_pay_at),
      '所属厂家': (() => {
        const code = String(device.manufacturer || '').trim();
        if (code === 'ZT') return '厂家一';
        if (code.toUpperCase() === 'BY') return '民一';
        return code || '-';
      })(),
      '设备详细类型': formatElectricDetailTypeLabel(device),
      '通讯类型': (() => {
        const t = Number(device?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      })(),
      '设备状态': device.device_status_name || '-',
      '电表余额(元)': Number(device.electric_balance || 0).toFixed(2),
      '剩余电量(kWh)': Number(device.electric_quantity || 0).toFixed(2),
      '电压(V)': device.voltage_v == null || Number.isNaN(Number(device.voltage_v)) ? '-' : Number(device.voltage_v).toFixed(3),
      '电流(A)': device.current_a == null || Number.isNaN(Number(device.current_a)) ? '-' : Number(device.current_a).toFixed(3),
      '功率(W)': device.active_power_w == null || Number.isNaN(Number(device.active_power_w)) ? '-' : Number(device.active_power_w).toFixed(3),
      '最低充值(元)': Number(device.min_recharge || 0).toFixed(2),
      '管理费(元)': Number(device.management_fee || 0).toFixed(2),
      '总用电量(kWh)': Number(device.total_electricity || 0).toFixed(2),
      '电价(元/kWh)': Number(device.electric_price || 0).toFixed(2),
      '绑定房间': device.room_name || '-',
      '所属房源': device.house_name || '-',
      '创建时间': device.created_at || '-',
      '更新时间': device.updated_at || '-',
    }));

    if (exportToCSV(exportData, '电表列表')) {
      message.success(`已导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 下载批量导入模板
  const handleDownloadTemplate = async () => {
    try {
      await downloadDeviceTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  // 批量导入设备
  const fileInputRef = useRef(null);
  const handleBatchImport = () => {
    // 触发文件选择
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 检查文件类型
    if (!file.name.endsWith('.csv')) {
      message.error('请选择CSV文件');
      return;
    }

    try {
      setLoading(true);
      const res = await batchImportDevices(file);
      showBatchImportResult(res, { entityName: '设备' });
      if (res?.success) {
        fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode);
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      // 清空文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ✅ 弹窗赋值：自动带入电表类型，隐藏类型选择
  const showModal = (device = null) => {
    setEditingDevice(device);
    if (device) {
      form.setFieldsValue({
        device_type: device.device_type != null ? Number(device.device_type) : undefined,
        device_mac: device.device_mac,
        factory_meter_no: device.factory_meter_no,
        current_meter_no: device.current_meter_no,
        communication_type: device.communication_type === 0 ? 0 : 1,
        // 仅 4G 设备才有“4G主表/蓝牙副表”详细类型；蓝牙设备这里保持为空
        device_detail_type: device.communication_type === 0 ? (device.device_detail_type === 1 ? 1 : 0) : undefined,
        device_status: device.device_status.toString(),
        electric_balance: device.electric_balance || 0,
        electric_quantity: device.electric_quantity || 0,
        min_recharge: device.min_recharge || 0,
        pay_daily_limit: device.pay_daily_limit ?? 1000,
        pay_monthly_limit: device.pay_monthly_limit ?? 5000,
        management_fee: device.management_fee ?? 0,
        electric_price: device.electric_price ?? 0,
        show_price: Number(device.show_price) === 1,
        show_management_fee: Number(device.show_management_fee) === 1,
        show_service_fee: Number(device.show_service_fee) === 1,
        bind_room_id: device.bind_room_id ? device.bind_room_id.toString() : '',
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // ✅ 提交时：新增只传当前表号，编辑保持原有逻辑
  const handleSubmit = async () => {
    try {
      if (editingDevice) {
        // 编辑模式：使用原有逻辑，保留所有字段
        const values = await form.validateFields();
        const communicationType = values.communication_type !== undefined && values.communication_type !== '' && values.communication_type != null
          ? parseInt(values.communication_type, 10)
          : 1;
        const submitData = {
          ...values,
          device_status: parseInt(values.device_status),
          bind_room_id: values.bind_room_id ? parseInt(values.bind_room_id) : 0,
          electric_balance: parseFloat(values.electric_balance) || 0,
          electric_quantity: parseFloat(values.electric_quantity) || 0,
          min_recharge: parseFloat(values.min_recharge) || 0,
          pay_daily_limit: parseFloat(values.pay_daily_limit) || 0,
          pay_monthly_limit: parseFloat(values.pay_monthly_limit) || 0,
          management_fee: parseFloat(values.management_fee) || 0,
          electric_price: parseFloat(values.electric_price) || 0,
          show_price: values.show_price ? 1 : 0,
          show_management_fee: values.show_management_fee ? 1 : 0,
          show_service_fee: values.show_service_fee ? 1 : 0,
          communication_type: communicationType,
          // 仅 4G 设备需要详细类型；蓝牙设备置空
          device_detail_type: communicationType === 0
            ? (values.device_detail_type !== undefined && values.device_detail_type !== '' && values.device_detail_type != null ? parseInt(values.device_detail_type, 10) : 0)
            : null,
        };
        // device_type：只有用户真的选了才传（没选=不修改，避免误覆盖/置空）
        if (values.device_type !== undefined && values.device_type !== '' && values.device_type != null) {
          submitData.device_type = parseInt(values.device_type, 10);
        } else {
          delete submitData.device_type;
        }
        // 允许编辑设备MAC（选填），保留在 submitData 中传给后端
        if (submitData.device_mac !== undefined && submitData.device_mac !== null) {
          submitData.device_mac = String(submitData.device_mac).trim() || null;
        }
        setLoading(true);
        const res = await updateDevice(editingDevice.id, submitData);
        res?.success ? message.success(`编辑${CURRENT_DEVICE_TYPE.label}成功`)
          : message.error(`编辑${CURRENT_DEVICE_TYPE.label}失败：` + (res?.error || '后端异常'));
        setIsModalVisible(false);
        fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode);
      } else {
        // 新增模式：只传当前表号和设备类型，其他使用默认值
        const values = await form.validateFields();
        const submitData = {
          current_meter_no: values.current_meter_no,
          device_type: parseInt(values.device_type), // 用户选择的设备类型（电表/水表/门锁）
          device_status: 1, // 默认：在线/正常
          device_mac: values.device_mac ? String(values.device_mac).trim() || undefined : undefined, // 选填
          // 其他字段不传，后端会使用默认值（余额=0，未绑定房间等）
        };
        setLoading(true);
        const res = await addDevice(submitData);
        const deviceTypeName = DEVICE_TYPE_OPTIONS.find(opt => opt.value === submitData.device_type)?.label || '设备';
        res?.success ? message.success(`新增${deviceTypeName}成功`)
          : message.error(`新增${deviceTypeName}失败：` + (res?.error || '后端异常'));
        setIsModalVisible(false);
        fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode);
      }
    } catch (error) {
      console.error('提交表单失败：', error);
      message.error('提交失败，请检查输入内容');
    } finally {
      setLoading(false);
    }
  };

  // 单个删除（复用原有）
  const handleDelete = async (id) => {
    if (!window.confirm(`确定要删除该${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await deleteDevice(id);
      message.success(`删除${CURRENT_DEVICE_TYPE.label}成功`);
      fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败：', error);
      message.error(`删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  // 批量删除（复用原有）
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(`请至少选择一个${CURRENT_DEVICE_TYPE.label}进行删除`);
      return;
    }
    if (!window.confirm(`确定要删除选中的${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await batchDeleteDevices(selectedRowKeys);
      message.success(`成功删除${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}`);
      fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('批量删除失败：', error);
      message.error(`批量删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  // 分页变化处理
  const handleTableChange = (newPagination) => {
    fetchPage(
      newPagination.current,
      newPagination.pageSize,
      appliedKeyword,
      appliedSearchScope,
      appliedMatchMode,
      appliedLocationKeyword,
      appliedLocationScope,
      appliedLocationMatchMode,
      appliedRoomBindStatus,
    );
  };

  const handleSupplementElectric = (record) => {
    setSupplementDevice(record);
  };

  const markElectricSupplementNeedsRead = (deviceId) => {
    setElectricMeterNeedReadIds((prev) => new Set(prev).add(deviceId));
  };
  const clearElectricSupplementNeedsRead = (deviceId) => {
    setElectricMeterNeedReadIds((prev) => {
      const next = new Set(prev);
      next.delete(deviceId);
      return next;
    });
  };

  const openLockConfirm = (record) => {
    const locked = Number(record?.is_locked) === 1;
    console.log('[电表锁定] 点击按钮', { id: record?.id, is_locked: record?.is_locked, locked });
    setLockTarget({
      record,
      locked,
      targetLock: locked ? 0 : 1,
    });
  };

  const submitLockToggle = async () => {
    if (!lockTarget?.record?.id) return;
    const { record, locked, targetLock } = lockTarget;
    setLockSubmitting(true);
    try {
      console.log('[电表锁定] 请求', { device_id: record.id, lock_status: targetLock });
      const res = await toggleAdminDeviceLock(record.id, targetLock);
      console.log('[电表锁定] 响应', res);
      if (res?.success) {
        message.success(locked ? '解锁成功' : '锁定成功');
        setLockTarget(null);
        fetchPage(pagination.current, pagination.pageSize);
      } else {
        message.error(res?.msg || res?.error || '操作失败');
      }
    } catch (e) {
      console.error('[电表锁定] 失败', e);
      message.error(e?.response?.data?.msg || e?.message || '操作失败');
    } finally {
      setLockSubmitting(false);
    }
  };

  // 原始表格列配置
  const originalColumns = [
    {
      title: '操作',
      key: 'action',
      width: 480,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
          <Button
            type="primary"
            danger
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openLockConfirm(record);
            }}
          >
            {Number(record?.is_locked) === 1 ? '该电表已锁定' : '锁定电表'}
          </Button>
          {isBYGatewayMeter(record) && (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={(e) => {
                e.stopPropagation();
                openBYGatewayRelation(record);
              }}
            >
              子母表绑定
            </Button>
          )}
          {isBYGatewayMeter(record) && Number(record?.house_id) > 0 && (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openBYHouseBatch(record);
              }}
            >
              本房源检查
            </Button>
          )}
          {isDevice4GMeter(record) && (
            <>
              {/* 与锁定按钮拉开间距，降低误触 */}
              <span style={{ display: 'inline-block', width: 16 }} />
              <Tooltip
                title={
                  electricMeterNeedReadIds.has(record.id)
                    ? '上次补量后建议先在弹窗内抄表核对（按钮仍可打开）'
                    : undefined
                }
              >
                <span>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isByManufacturer(record)) {
                        setByDevTip({ open: true, text: '民一远程补电量功能开发中，暂不可用。' });
                        return;
                      }
                      handleSupplementElectric(record);
                    }}
                  >
                    补电量
                  </Button>
                </span>
              </Tooltip>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isByManufacturer(record)) {
                    setByDevTip({ open: true, text: '民一远程开关闸功能开发中，暂不可用。' });
                    return;
                  }
                  setSwitchDevice(record);
                }}
              >
                开关闸
              </Button>
              {!isByManufacturer(record) && (
                <>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', color: '#fa8c16' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRebootDevice(record);
                    }}
                  >
                    重启
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', color: '#ff4d4f' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMeteringFactorDevice(record);
                    }}
                  >
                    计量系数
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', color: '#1890ff' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMeteringTotalDevice(record);
                    }}
                  >
                    计量总额
                  </Button>
                </>
              )}
            </>
          )}
        </Space>
      ),
    },
    { title: '电表ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '设备MAC', dataIndex: 'device_mac', key: 'device_mac', width: 140 },
    { title: '厂商表号', dataIndex: 'factory_meter_no', key: 'factory_meter_no', width: 130 },
    {
      title: '最新RX主题',
      dataIndex: 'latest_rx_topic',
      key: 'latest_rx_topic',
      width: 200,
      ellipsis: true,
      render: (v, record) => {
        if (!isDevice4GMeter(record)) return '-';
        const topic = String(getLatestRxTopic(record) || v || '').trim();
        if (!topic) return '-';
        return (
          <Tooltip title={topic}>
            <Text copyable={{ text: topic }} style={{ fontSize: 12, color: '#1677ff' }}>
              {topic}
            </Text>
          </Tooltip>
        );
      },
    },
    { title: '当前表号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 130 },
    {
      title: '通讯类型',
      dataIndex: 'communication_type',
      key: 'communication_type',
      width: 100,
      render: (_, record) => {
        const t = Number(record?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      },
    },
    { title: '在线状态', dataIndex: 'is_online_name', key: 'is_online_name', width: 100 },
    {
      title: 'ICCID',
      dataIndex: 'iccid',
      key: 'iccid',
      width: 200,
      render: (v, record) => (isDevice4GMeter(record) && v ? String(v) : '-'),
    },
    {
      title: '信号强度',
      dataIndex: 'signal_rssi',
      key: 'signal_rssi',
      width: 90,
      render: (_v, record) => formatYjRssiOriginal(record),
    },
    {
      title: '信号强度(换算)',
      dataIndex: 'signal_strength_bars',
      key: 'signal_strength_bars',
      width: 110,
      render: (v, record) => {
        if (!isDevice4GMeter(record)) return '-';
        const hasTime = record.last_signal_at != null && String(record.last_signal_at).trim() !== '';
        const bars = v != null && String(v).trim() !== '' ? String(v).trim() : '-';
        if (hasTime || bars !== '-') return bars;
        return '-';
      },
    },
    {
      title: '上次信号时间',
      dataIndex: 'last_signal_at',
      key: 'last_signal_at',
      width: 170,
      render: (v, record) => (isDevice4GMeter(record) ? formatLastSignalAt(v) : '-'),
    },
    {
      title: '上次充值时间',
      dataIndex: 'last_electric_pay_at',
      key: 'last_electric_pay_at',
      width: 170,
      render: (v) => formatLastElectricPayAt(v),
    },
    {
      title: '设备厂家',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 140,
      render: (v) => {
        const code = String(v || '').trim();
        if (code === 'ZT') return '厂家一';
        if (code.toUpperCase() === 'BY') return '民一';
        return code || '-';
      },
    },
    { title: 'IMEI', dataIndex: 'imei', key: 'imei', width: 160, render: (v) => (v ? String(v) : '-') },
    {
      title: '设备详细类型',
      dataIndex: 'device_detail_type',
      key: 'device_detail_type',
      width: 120,
      render: (v, record) => formatElectricDetailTypeLabel(record) || '',
    },
    { title: '设备状态', dataIndex: 'device_status_name', key: 'device_status_name', width: 90 },
    { title: '电表余额(元)', dataIndex: 'electric_balance', key: 'electric_balance', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '剩余电量(kWh)', dataIndex: 'electric_quantity', key: 'electric_quantity', width: 110, render: (v) => (v || 0).toFixed(2) },
    {
      title: '电压(V)',
      dataIndex: 'voltage_v',
      key: 'voltage_v',
      width: 90,
      render: (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(3)),
    },
    {
      title: '电流(A)',
      dataIndex: 'current_a',
      key: 'current_a',
      width: 90,
      render: (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(3)),
    },
    {
      title: '功率(W)',
      dataIndex: 'active_power_w',
      key: 'active_power_w',
      width: 90,
      render: (v) => (v == null || Number.isNaN(Number(v)) ? '-' : Number(v).toFixed(3)),
    },
    { title: '最低充值(元)', dataIndex: 'min_recharge', key: 'min_recharge', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '管理费(元/度)', dataIndex: 'management_fee', key: 'management_fee', width: 110, render: (v) => (Number(v) || 0).toFixed(3) },
    { title: '总用电量(kWh)', dataIndex: 'total_electricity', key: 'total_electricity', width: 110, render: (v) => (v || 0).toFixed(2) },
    { title: '电价(元/kWh)', dataIndex: 'electric_price', key: 'electric_price', width: 110, render: (v) => (Number(v) || 0).toFixed(3) },
    { title: '绑定房间', dataIndex: 'room_name', key: 'room_name', width: 100 },
    { title: '所属房源', dataIndex: 'house_name', key: 'house_name', width: 150 },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '删除',
      key: 'delete_action',
      width: 88,
      render: (_, record) => (
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
          删除
        </Button>
      ),
    },
  ];

  let electricColumnStates = {};
  try {
    const raw = localStorage.getItem('electricMeterList_columnStates');
    if (raw) electricColumnStates = JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse column states:', e);
  }
  const electricResizableColumns = originalColumns
    .map((col) => (electricColumnStates[col.key] === 'hidden' ? null : col))
    .filter(Boolean);

  const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title={`${CURRENT_DEVICE_TYPE.label}管理`} />

      {/* 统一的工具栏 */}
      <ListToolbar
        showMatchMode
        matchMode={matchMode}
        matchModeOptions={DEVICE_CODE_MATCH_MODE_OPTIONS}
        onMatchModeChange={handleMatchModeChange}
        searchScopeOptions={ELECTRIC_METER_SEARCH_SCOPE_OPTIONS}
        searchScope={searchScope}
        onSearchScopeChange={handleSearchScopeChange}
        showLocationSearch
        locationPlaceholder={locationSearchPlaceholder}
        locationValue={locationText}
        onLocationChange={setLocationText}
        locationScope={locationScope}
        onLocationScopeChange={handleLocationScopeChange}
        locationMatchMode={locationMatchMode}
        onLocationMatchModeChange={handleLocationMatchModeChange}
        searchPlaceholder={electricSearchPlaceholder}
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        beforeFilters={(
          <Space size={6} wrap>
            <span style={{ color: '#666', fontSize: 12 }}>上次充值</span>
            <Input
              type="date"
              size="small"
              style={{ width: 138 }}
              value={payStartDate}
              onChange={handlePayStartDateChange}
              title="开始日期"
              allowClear
            />
            <span style={{ color: '#999', fontSize: 12 }}>至</span>
            <Input
              type="date"
              size="small"
              style={{ width: 138 }}
              value={payEndDate}
              onChange={handlePayEndDateChange}
              title="结束日期"
              allowClear
            />
            <Button size="small" onClick={() => handlePayRecentMonths(3)}>近3个月</Button>
          </Space>
        )}
        filters={[
          {
            key: 'room_bind',
            label: '房间绑定',
            value: roomBindStatus === 'all' ? undefined : roomBindStatus,
            onChange: handleRoomBindStatusChange,
            options: [
              { value: 'all', label: '全部' },
              { value: 'bound', label: '已绑定房间' },
              { value: 'unbound', label: '未绑定房间' },
            ],
          },
          {
            key: 'remote_status',
            label: '远程筛选',
            value: remoteStatus === 'all' ? undefined : remoteStatus,
            onChange: handleRemoteStatusChange,
            options: [
              { value: 'all', label: '全部' },
              { value: 'remote', label: '远程' },
              { value: 'bluetooth', label: '蓝牙' },
              { value: 'online', label: '远程在线' },
              { value: 'offline', label: '远程离线' },
            ],
          },
          {
            key: 'meter_vendor',
            label: '表类型',
            value: meterVendor === 'all' ? undefined : meterVendor,
            onChange: handleMeterVendorChange,
            options: [
              { value: 'all', label: '全部' },
              { value: 'yj', label: 'YJ' },
              { value: 'by', label: '民一' },
            ],
          },
          {
            key: 'remain_status',
            label: '剩余电量',
            value: remainStatus === 'all' ? undefined : remainStatus,
            onChange: handleRemainStatusChange,
            options: [
              { value: 'all', label: '全部' },
              { value: 'negative', label: '负数' },
            ],
          },
        ]}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={handleBatchDelete}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default'
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default'
          }
        ]}
      />

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ResizableTable
        columns={electricResizableColumns.length > 0 ? electricResizableColumns : originalColumns}
        dataSource={devices}
        loading={loading}
        rowKey="id"
        storageKey="electricMeterList"
        rowSelection={rowSelection}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) =>
            `共 ${total} 条 · 已绑定房间 ${boundCount} 个 · 未绑定房间 ${unboundCount} 个 · 远程共 ${remoteCount} 个 · YJ远程在线 ${remoteOnlineCount} 个`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 2200 }}
        onChange={handleTableChange}
        locale={{ emptyText: <Empty description={`暂无${CURRENT_DEVICE_TYPE.label}数据`} /> }}
      />
      <Modal
        title="开发中"
        open={byDevTip.open}
        onCancel={() => setByDevTip({ open: false, text: '' })}
        onOk={() => setByDevTip({ open: false, text: '' })}
        okText="知道了"
        cancelButtonProps={{ style: { display: 'none' } }}
        maskClosable
      >
        <p style={{ margin: 0 }}>{byDevTip.text || '功能开发中，暂不可用。'}</p>
      </Modal>
      <Modal
        title={`BY 子母表绑定${byRelationModal.relation?.meter_no ? ` · ${byRelationModal.relation.meter_no}` : ''}`}
        open={byRelationModal.open}
        onCancel={() => !byRelationSubmitting && setByRelationModal({ open: false, target: null, relation: null, children: [] })}
        footer={<Button onClick={() => setByRelationModal({ open: false, target: null, relation: null, children: [] })} disabled={byRelationSubmitting}>关闭</Button>}
        width={680}
        maskClosable={!byRelationSubmitting}
        destroyOnClose
      >
        {byRelationLoading ? (
          <div>正在查询厂家绑定关系…</div>
        ) : byRelationModal.relation?.error ? (
          <Space direction="vertical">
            <Text type="danger">{byRelationModal.relation.error}</Text>
            <Button onClick={refreshBYGatewayRelation}>重新查询</Button>
          </Space>
        ) : byRelationModal.relation?.relation_type === 'mother' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>母表查询结果仅展示，不会自动改写数据库；异常关系请执行“修复绑定”。</Text>
            <Button onClick={refreshBYGatewayRelation} loading={byRelationLoading}>验证网络</Button>
            {byRelationModal.children.length ? byRelationModal.children.map((child) => (
              <div key={`${child.device_id}-${child.meter_no}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                <div style={{ flex: 1 }}>
                  <div>{child.meter_no}</div>
                  <Text type={child.bind_success ? 'success' : 'danger'}>{child.bind_success ? '已绑定' : (child.bind_reason || '未绑定成功')}</Text>
                </div>
                {child.device_id > 0 && !child.bind_success && (
                  <Button size="small" type="primary" loading={byRelationSubmitting} onClick={() => submitBYGatewayAction('repair', child.device_id, byRelationModal.target?.id)}>修复绑定</Button>
                )}
                {child.device_id > 0 && (
                  <Button size="small" danger loading={byRelationSubmitting} onClick={() => requestBYGatewayUnbind(child.device_id, child.meter_no)}>解绑</Button>
                )}
              </div>
            )) : <Empty description="厂家未返回子表" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </Space>
        ) : byRelationModal.relation?.relation_type === 'child' ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>{byRelationModal.relation?.bound_mother?.meter_no ? `当前数据库记录母表：${byRelationModal.relation.bound_mother.meter_no}` : '当前未记录母表'}</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="选择要绑定的母表"
              value={bySelectedMotherId}
              onChange={setBySelectedMotherId}
              options={(byRelationModal.relation?.available_mothers || []).map((mother) => ({
                value: mother.device_id,
                label: `${mother.meter_no}（${mother.room_name || '-'}）`,
              }))}
            />
            <Space wrap>
              <Button type="primary" disabled={!bySelectedMotherId} loading={byRelationSubmitting} onClick={() => submitBYGatewayAction('bind', byRelationModal.target?.id, bySelectedMotherId)}>绑定母表</Button>
              <Button disabled={!bySelectedMotherId} loading={byRelationSubmitting} onClick={() => submitBYGatewayAction('repair', byRelationModal.target?.id, bySelectedMotherId)}>修复绑定</Button>
              {byRelationModal.relation?.bound_mother?.meter_no && (
                <Button danger loading={byRelationSubmitting} onClick={() => requestBYGatewayUnbind(byRelationModal.target?.id, byRelationModal.relation?.meter_no)}>解绑母表</Button>
              )}
              <Button onClick={refreshBYGatewayRelation} loading={byRelationLoading}>刷新关系</Button>
            </Space>
          </Space>
        ) : (
          <Empty description="未识别到子母表关系" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Modal>
      <Modal
        title="解除子母表绑定"
        open={!!byUnbindTarget}
        onCancel={() => !byRelationSubmitting && setByUnbindTarget(null)}
        onOk={confirmBYGatewayUnbind}
        okText="确认解绑"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={byRelationSubmitting}
        closable={!byRelationSubmitting}
        maskClosable={!byRelationSubmitting}
        zIndex={1100}
      >
        <p style={{ margin: 0 }}>确认解除子表 {byUnbindTarget?.meterNo || '-'} 的厂家绑定关系吗？</p>
      </Modal>
      <Modal
        title={`BY 房源绑定检查${byHouseBatch.houseName ? ` · ${byHouseBatch.houseName}` : ''}`}
        open={byHouseBatch.open}
        onCancel={() => !byHouseBatch.repairing && setByHouseBatch((prev) => ({ ...prev, open: false }))}
        footer={
          <Space>
            <Button onClick={() => setByHouseBatch((prev) => ({ ...prev, open: false }))}>关闭</Button>
            <Button type="primary" danger disabled={!byHouseBatch.data?.repairable_count} loading={byHouseBatch.repairing} onClick={repairBYHouseBatch}>
              一键修复 {byHouseBatch.data?.repairable_count || 0} 项
            </Button>
          </Space>
        }
        width={760}
      >
        {byHouseBatch.loading ? (
          <div>正在逐个查询母表厂家关系…</div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              母表 {byHouseBatch.data?.mother_count || 0} 个；可修复 {byHouseBatch.data?.repairable_count || 0} 项；查询失败母表 {byHouseBatch.data?.query_failed_mother_count || 0} 个。
            </Text>
            {(byHouseBatch.data?.items || []).map((item, index) => (
              <div key={`${item.parent_device_id}-${item.child_device_id}-${index}`} style={{ padding: 8, borderBottom: '1px solid #f0f0f0' }}>
                <Text>{item.child_meter_no || '-'} → {item.parent_meter_no}</Text>
                <br />
                <Text type={item.can_repair ? 'warning' : item.status === 'bound' ? 'success' : 'secondary'}>
                  {item.reason || (item.status === 'bound' ? '已绑定' : item.status)}
                </Text>
              </div>
            ))}
          </Space>
        )}
      </Modal>
      <Modal
        title={lockTarget?.locked ? '解锁电表' : '锁定电表'}
        open={!!lockTarget}
        onCancel={() => !lockSubmitting && setLockTarget(null)}
        onOk={submitLockToggle}
        okText="确定"
        cancelText="取消"
        confirmLoading={lockSubmitting}
        okButtonProps={lockTarget?.locked ? undefined : { danger: true }}
        destroyOnClose
        maskClosable={false}
      >
        <p>
          {lockTarget?.locked
            ? '解锁后，租客将可以对该电表进行充值。确定要解锁该电表吗？'
            : '锁定后，租客将无法对该电表进行充值，房东端无法自行解锁，也无法手动通电。确定要锁定该电表吗？'}
        </p>
      </Modal>
      <Modal
        title={editingDevice ? `编辑${CURRENT_DEVICE_TYPE.label}` : '新增设备'}
        open={isModalVisible}
        onCancel={() => {
          setServiceFeeConfirmOpen(false);
          setIsModalVisible(false);
        }}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          {editingDevice ? (
            // 编辑模式：显示所有字段
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="factory_meter_no"
                label="厂商表号"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入厂商表号" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号" maxLength={50} />
              </Form.Item>
              <Form.Item name="communication_type" label="通讯类型">
                <Select placeholder="不填默认蓝牙" allowClear options={[
                  { value: 0, label: '4G' },
                  { value: 1, label: '蓝牙' },
                ]} />
              </Form.Item>
              <Form.Item name="device_detail_type" label="设备详细类型">
                <Select
                  placeholder={Number(watchedCommunicationType) === 0 ? '不填默认蓝牙副表' : '蓝牙设备无详细类型'}
                  allowClear
                  disabled={Number(watchedCommunicationType) !== 0}
                  options={[
                    { value: 0, label: '蓝牙副表' },
                    { value: 1, label: '4G主表' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="device_status"
                label="设备状态"
                rules={[{ required: true, message: '请选择设备状态' }]}
              >
                <Select placeholder="请选择设备状态" options={DEVICE_STATUS_OPTIONS} />
              </Form.Item>
              <Form.Item name="bind_room_id" label="绑定房间">
                <Select
                  placeholder="先输入关键词再选房间（房源名/房号）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const q = String(input ?? '').trim().toLowerCase();
                    if (!q) return false;
                    return String(option?.label ?? '')
                      .toLowerCase()
                      .includes(q);
                  }}
                >
                  {rooms.map((room) => {
                    const label =
                      (room.houseName ?? room.house_name ?? '-') +
                      ' - ' +
                      (room.number ?? room.fang_hao ?? '-');
                    return (
                      <Option key={room.id} value={room.id.toString()} label={label}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="electric_balance"
                  label="电表余额（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="electric_quantity"
                  label="剩余电量（kWh）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效电量（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="min_recharge"
                  label="最低充值（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="management_fee"
                  label="管理费（元/度）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,3})?$/, message: '请输入有效金额（最多三位小数）' }]}
                >
                  <Input placeholder="如 0.050" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="pay_daily_limit"
                  label="每日支付限额（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认1000" />
                </Form.Item>
                <Form.Item
                  name="pay_monthly_limit"
                  label="每月支付限额（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认5000" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="electric_price"
                  label="电价（元/kWh）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,3})?$/, message: '请输入有效单价（最多三位小数）' }]}
                >
                  <Input placeholder="如 0.560" />
                </Form.Item>
                <Form.Item label="总用电量（kWh）" style={{ flex: 1 }}>
                  <Input disabled value={(editingDevice.total_electricity || 0).toFixed(2)} />
                </Form.Item>
              </Space.Compact>
              <Divider orientation="left" style={{ margin: '8px 0 16px', fontSize: 13, color: '#666' }}>
                租客端显示（与小程序 test4 一致，不写入操作记录）
              </Divider>
              <Form.Item
                name="show_price"
                label="显示单价"
                valuePropName="checked"
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
              <Form.Item
                name="show_management_fee"
                label="显示管理费"
                valuePropName="checked"
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
              <Form.Item
                name="show_service_fee"
                label="显示服务费"
                valuePropName="checked"
                getValueFromEvent={(checked) => {
                  if (checked) {
                    setServiceFeeConfirmOpen(true);
                    return false;
                  }
                  return false;
                }}
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
            </>
          ) : (
            // 新增模式：设备类型、当前表号（必填），设备MAC（选填）
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号（必填）" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <div style={{ color: '#999', fontSize: '12px', marginTop: '-16px', marginBottom: '16px' }}>
                其他字段将使用默认值：设备状态=在线/正常，余额=0，未绑定房间
              </div>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="提示"
        open={serviceFeeConfirmOpen}
        okText="确定"
        cancelText="取消"
        zIndex={3000}
        getContainer={() => document.body}
        maskClosable={false}
        onOk={() => {
          form.setFieldsValue({ show_service_fee: true });
          setServiceFeeConfirmOpen(false);
        }}
        onCancel={() => {
          form.setFieldsValue({ show_service_fee: false });
          setServiceFeeConfirmOpen(false);
        }}
      >
        是否允许租客查看电表服务费？开启后租客将可见。
      </Modal>

      <MeterSupplementModal
        open={!!supplementDevice}
        device={
          supplementDevice
            ? devices.find((d) => d.id === supplementDevice.id) || supplementDevice
            : null
        }
        variant="electric"
        supplementBlockedUntilRead={
          supplementDevice ? electricMeterNeedReadIds.has(supplementDevice.id) : false
        }
        onSupplementIssued={markElectricSupplementNeedsRead}
        onMeterReadSuccess={clearElectricSupplementNeedsRead}
        onClose={() => {
          if (supplementDevice?.id != null) clearElectricSupplementNeedsRead(supplementDevice.id);
          setSupplementDevice(null);
        }}
        onRefresh={() => fetchPage(pagination.current, pagination.pageSize, appliedKeyword, appliedSearchScope, appliedMatchMode)}
      />

      <MeterSwitchModal
        open={!!switchDevice}
        device={switchDevice}
        onClose={() => setSwitchDevice(null)}
      />

      <MeterRebootModal
        open={!!rebootDevice}
        device={rebootDevice}
        onClose={() => setRebootDevice(null)}
      />

      <MeterMeteringFactorModal
        open={!!meteringFactorDevice}
        device={
          meteringFactorDevice
            ? devices.find((d) => d.id === meteringFactorDevice.id) || meteringFactorDevice
            : null
        }
        variant="electric"
        onClose={() => setMeteringFactorDevice(null)}
      />

      <MeterMeteringTotalModal
        open={!!meteringTotalDevice}
        device={
          meteringTotalDevice
            ? devices.find((d) => d.id === meteringTotalDevice.id) || meteringTotalDevice
            : null
        }
        variant="electric"
        onClose={() => setMeteringTotalDevice(null)}
      />
    </div>
  );
};
export default ElectricMeterList;